import { MenuItem } from '@material-ui/core';
import { Heading } from 'copilot-design-system';
import { FormikErrors, FormikTouched, useFormikContext } from 'formik';
import React, { useContext } from 'react';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

import { RouteContext } from 'src/context';
import { useAppSelector } from 'src/hooks/useStore';
import {
  GetCurrencyPrefix,
  GetDefaultCurrency,
} from 'src/legacy/components/Formatters';
import { FormModal } from 'src/legacy/components/Modals';
import { PriceTypeToggle } from 'src/legacy/components/Products/PriceTypeToggle';
import { getRenderCurrencyPrefixProps } from 'src/legacy/components/Products/ProductEditPage/ProductEditPage';
import {
  BaseTextField,
  CurrencyTextField,
} from 'src/legacy/components/TextField';
import {
  MAX_LINE_ITEM_AMOUNT,
  MIN_LINE_ITEM_AMOUNT,
} from 'src/services/api/invoicesApi';
import {
  BILLING_PERIODS,
  PriceType,
  useAddPriceByProductIdMutation,
} from 'src/services/api/productsApi';
import { SubscriptionInterval } from 'src/types/subscriptions';

type Props = {
  open: boolean;
  onCloseModal: () => void;
};

const basePriceSchema = z.object({
  productId: z.string(),
  amount: z.string({ required_error: 'Price cannot be empty' }).refine(
    (value) => {
      const number = parseFloat(value);
      return number >= MIN_LINE_ITEM_AMOUNT && number <= MAX_LINE_ITEM_AMOUNT;
    },
    {
      message: 'Price must be between 0.5 and 100,000',
    },
  ),
  currency: z.string(),
});

const oneTimePriceSchema = basePriceSchema.extend({
  type: z.literal(PriceType.OneTime),
});

const recurringPriceSchema = basePriceSchema.extend({
  type: z.literal(PriceType.Recurring),
  interval: z.enum(
    [
      SubscriptionInterval.Day,
      SubscriptionInterval.Week,
      SubscriptionInterval.Month,
      SubscriptionInterval.Quarterly,
      SubscriptionInterval.Biannually,
      SubscriptionInterval.Year,
      SubscriptionInterval.SemiAnnually,
    ],
    { required_error: 'Billing period must be selected' },
  ),
});

export const priceSchema = z.discriminatedUnion('type', [
  oneTimePriceSchema,
  recurringPriceSchema,
]);

type OneTimePriceSchema = z.infer<typeof oneTimePriceSchema>;
type RecurringPriceSchema = z.infer<typeof recurringPriceSchema>;

export type PriceSchema = z.infer<typeof priceSchema>;

const useForm = () => {
  const FormRenderer = () => {
    const accountInfo = useAppSelector((state) => state.payments.accountInfo);

    const defaultCurrency =
      accountInfo?.default_currency || GetDefaultCurrency().toLocaleLowerCase();
    return <PriceForm defaultCurrency={defaultCurrency} />;
  };

  return {
    FormRenderer,
    validationScheme: toFormikValidationSchema(priceSchema),
  };
};

export const AddPrice: React.FC<Props> = ({ open, onCloseModal }) => {
  const { pathname } = useContext(RouteContext);
  const productId = pathname.split('/').pop();
  const accountInfo = useAppSelector((state) => state.payments.accountInfo);

  const defaultCurrency =
    accountInfo?.default_currency || GetDefaultCurrency().toLowerCase();

  const [addPrice] = useAddPriceByProductIdMutation();

  const handleAddPrice = async (values: PriceSchema) => {
    const result = await addPrice(values);
    if ('data' in result) {
      onCloseModal();
    }
  };

  const handleCloseModal = () => {
    onCloseModal();
  };

  if (!productId) {
    return null;
  }

  return (
    <FormModal
      title="Add a price"
      buttonLabel="Add a price"
      initialFormValue={{
        productId,
        amount: '',
        type: PriceType.OneTime,
        currency: defaultCurrency,
      }}
      entityName="price"
      handleSave={handleAddPrice}
      useFormHook={useForm}
      open={open}
      onClose={handleCloseModal}
      createButtonProps={{ fullWidth: true }}
      skipResetOnSubmit
    />
  );
};

const isRecurringPriceTouched = (
  _touched: FormikTouched<OneTimePriceSchema | RecurringPriceSchema>,
  values: OneTimePriceSchema | RecurringPriceSchema | undefined,
): _touched is FormikTouched<RecurringPriceSchema> => {
  return values?.type === PriceType.Recurring;
};

const isRecurringPriceError = (
  _errors: FormikErrors<OneTimePriceSchema | RecurringPriceSchema>,
  values: OneTimePriceSchema | RecurringPriceSchema | undefined,
): _errors is FormikErrors<RecurringPriceSchema> => {
  return values?.type === PriceType.Recurring;
};

const PriceForm = ({ defaultCurrency }: { defaultCurrency: string }) => {
  const { touched, errors, handleBlur, handleChange, values, setFieldValue } =
    useFormikContext<PriceSchema>();
  const prefix = GetCurrencyPrefix({ currency: defaultCurrency });
  const isBillingPeriodTouched =
    isRecurringPriceTouched(touched, values) && touched.interval;
  const billingPeriodErrorHelperText =
    isBillingPeriodTouched &&
    isRecurringPriceError(errors, values) &&
    errors.interval;

  const onToggleType = (tab: PriceType) => {
    setFieldValue('type', tab);
    if (tab === PriceType.Recurring) {
      setFieldValue('interval', SubscriptionInterval.Month);
    } else {
      setFieldValue('interval', undefined);
    }
  };

  const isRecurringPrice = values.type === PriceType.Recurring;

  return (
    <div>
      <div className="mb-4">
        <PriceTypeToggle type={values.type} onToggleType={onToggleType} />
      </div>
      <Heading className="mb-1 text-black-heading">Price</Heading>
      <div className="flex flex-col gap-4">
        <CurrencyTextField
          name="amount"
          variant="outlined"
          fullWidth
          onBlur={handleBlur}
          onChange={handleChange}
          InputProps={getRenderCurrencyPrefixProps(prefix || '')}
          value={values.amount}
          error={Boolean(touched.amount && errors.amount)}
          helperText={touched.amount && errors.amount}
          currency={defaultCurrency}
        />
      </div>
      {isRecurringPrice && (
        <div className="mt-4">
          <Heading className="mb-1 text-black-heading">Billing period</Heading>
          <BaseTextField
            select
            name="interval"
            variant="outlined"
            fullWidth
            value={values.interval}
            onBlur={handleBlur}
            onChange={handleChange}
            error={Boolean(
              isBillingPeriodTouched && billingPeriodErrorHelperText,
            )}
            helperText={billingPeriodErrorHelperText}
            additionalSelectProps={{
              classes: {
                root: 'text-base text-black-heading font-normal !h-[38px]',
              },
            }}
            className="[&>div>svg]:h-4 [&>div>svg]:w-4 [&>div>svg]:mt-1.5 "
          >
            {BILLING_PERIODS.map((period) => (
              <MenuItem value={period.value} key={period.value}>
                {period.label}
              </MenuItem>
            ))}
          </BaseTextField>
        </div>
      )}
    </div>
  );
};
